import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Gift,
  Plus,
  Copy,
  Check,
  Trash2,
  Loader2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * GiftCodesPanel — admin tool for issuing founding-member gift codes.
 *
 * Workflow:
 *   1. Pick months + label, click "Mint code"
 *   2. Copy the code to clipboard
 *   3. DM it personally to the recipient with whatever message you want
 *   4. Recipient pastes it in Settings → "Have a gift code?"
 *
 * Less surface area than an email blast (no SMTP failure path, no
 * spam risk, no auto-generated copy that doesn't feel personal). The
 * admin owns the moment of generosity, not the system.
 */
const PREFIX_HINT = "FOUNDER";

const GiftCodesPanel = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [months, setMonths] = useState(12);
  const [prefix, setPrefix] = useState(PREFIX_HINT);
  const [label, setLabel] = useState("Founding member");
  const [singleUse, setSingleUse] = useState(true);
  const [copiedCode, setCopiedCode] = useState(null);

  const headers = () => ({
    withCredentials: true,
  });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/gift-codes`, headers());
      setCodes(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load gift codes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const mint = async () => {
    if (months < 1 || months > 24) {
      toast.error("Months must be between 1 and 24.");
      return;
    }
    setCreating(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/gift-codes`,
        {
          months: Number(months),
          prefix: prefix.toUpperCase().replace(/[^A-Z0-9]/g, "") || PREFIX_HINT,
          single_use: singleUse,
          label: label || "Founding member",
        },
        headers(),
      );
      setCodes((prev) => [data, ...prev]);
      toast.success(`Minted ${data.code} — copy it and DM it. 💜`);
      // Auto-copy the freshly minted code
      try {
        await navigator.clipboard.writeText(data.code);
        setCopiedCode(data.code);
        setTimeout(() => setCopiedCode(null), 1800);
      } catch {
        /* clipboard may not be available; the toast still confirms */
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't mint code.");
    } finally {
      setCreating(false);
    }
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 1800);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Couldn't copy — long-press to copy manually.");
    }
  };

  const revoke = async (code) => {
    if (!window.confirm(`Revoke ${code}? Anyone holding it will get a friendly error.`)) {
      return;
    }
    try {
      const { data } = await axios.delete(
        `${API}/admin/gift-codes/${encodeURIComponent(code)}`,
        headers(),
      );
      setCodes((prev) => prev.map((c) => (c.code === code ? data : c)));
      toast.success(`Revoked ${code}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't revoke that code.");
    }
  };

  return (
    <div data-testid="gift-codes-panel" className="space-y-6">
      {/* Mint form */}
      <div className="rounded-2xl bg-zinc-900/60 border border-zinc-800 p-5 space-y-4">
        <div className="flex items-center gap-2 text-amber-300">
          <Gift className="h-4 w-4" />
          <span className="text-xs uppercase tracking-[0.18em] font-medium">
            Mint a gift code
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">
              Months of Pro (1–24)
            </label>
            <input
              type="number"
              min={1}
              max={24}
              value={months}
              onChange={(e) => setMonths(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm focus:outline-none focus:border-amber-400/50"
              data-testid="gift-mint-months"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">
              Prefix
            </label>
            <input
              type="text"
              value={prefix}
              onChange={(e) =>
                setPrefix(e.target.value.toUpperCase().slice(0, 20))
              }
              maxLength={20}
              className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm font-mono focus:outline-none focus:border-amber-400/50"
              data-testid="gift-mint-prefix"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">
              Label (private — for your own ledger)
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={120}
              placeholder="Founding member"
              className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm focus:outline-none focus:border-amber-400/50"
              data-testid="gift-mint-label"
            />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="inline-flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <input
              type="checkbox"
              checked={singleUse}
              onChange={(e) => setSingleUse(e.target.checked)}
              className="rounded accent-amber-400"
              data-testid="gift-mint-single-use"
            />
            Single use (recommended for personal gifts)
          </label>
          <button
            type="button"
            onClick={mint}
            disabled={creating}
            className="ml-auto inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-zinc-900 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60"
            data-testid="gift-mint-submit"
          >
            {creating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            Mint code
          </button>
        </div>
      </div>

      {/* List */}
      <div className="rounded-2xl bg-zinc-900/60 border border-zinc-800 overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-800 text-xs uppercase tracking-[0.18em] text-zinc-400 font-medium">
          Issued codes
        </div>
        {loading ? (
          <div className="px-5 py-6 flex items-center gap-2 text-zinc-400 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : codes.length === 0 ? (
          <div className="px-5 py-6 text-zinc-400 text-sm" data-testid="gift-codes-empty">
            No codes yet. Mint one above and DM it to someone you want to thank.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-800" data-testid="gift-codes-list">
            {codes.map((c) => {
              const used = (c.redeemed_by || []).length;
              const isUsedUp = c.single_use && used > 0;
              return (
                <motion.li
                  key={c.code}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`px-5 py-3.5 flex items-center gap-4 ${
                    c.revoked || isUsedUp ? "opacity-60" : ""
                  }`}
                  data-testid={`gift-code-row-${c.code}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="text-amber-300 font-mono text-sm tracking-wider">
                        {c.code}
                      </code>
                      <span className="text-[10px] uppercase tracking-wider bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded-full">
                        {c.months} mo
                      </span>
                      {c.single_use ? (
                        <span className="text-[10px] uppercase tracking-wider bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-full">
                          single use
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider bg-amber-400/10 text-amber-300 px-2 py-0.5 rounded-full">
                          multi · {used} redeemed
                        </span>
                      )}
                      {c.revoked && (
                        <span className="text-[10px] uppercase tracking-wider bg-rose-500/15 text-rose-300 px-2 py-0.5 rounded-full">
                          revoked
                        </span>
                      )}
                      {isUsedUp && !c.revoked && (
                        <span className="text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-300 px-2 py-0.5 rounded-full">
                          claimed
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1 truncate">
                      {c.label || "Founding member"}
                      {" · "}
                      Created {c.created_at?.slice(0, 10)}
                      {c.redeemed_at && (
                        <span> · Redeemed {c.redeemed_at?.slice(0, 10)}</span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => copy(c.code)}
                    className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-amber-300 transition-colors"
                    data-testid={`gift-code-copy-${c.code}`}
                    aria-label={`Copy ${c.code}`}
                  >
                    {copiedCode === c.code ? (
                      <Check className="h-4 w-4 text-emerald-300" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                  {!c.revoked && !isUsedUp && (
                    <button
                      type="button"
                      onClick={() => revoke(c.code)}
                      className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-rose-300 transition-colors"
                      data-testid={`gift-code-revoke-${c.code}`}
                      aria-label={`Revoke ${c.code}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </motion.li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="text-xs text-zinc-500 leading-relaxed">
        <Sparkles className="inline h-3 w-3 text-amber-300 mr-1" />
        Codes are case-insensitive. DM them in your own voice — recipients
        paste theirs in Settings → "Have a gift code?" and instantly get
        their Pro months added on top of whatever's already on their account.
      </p>
    </div>
  );
};

export default GiftCodesPanel;
